import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { LogIn, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../../context/auth";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" {...props}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export default function AuthModal() {
  const { authModalOpen, closeAuthModal, login, emailLogin, emailRegister, forgotPassword } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setError(""); setNotice(""); setPassword(""); };

  const switchMode = (m) => { setMode(m); reset(); };

  const handleClose = () => { closeAuthModal(); setTimeout(() => { switchMode("signin"); setEmail(""); }, 200); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "signin") {
        await emailLogin(email.trim(), password);
        handleClose();
      } else if (mode === "signup") {
        await emailRegister(email.trim(), password, name.trim());
        handleClose();
      } else if (mode === "forgot") {
        await forgotPassword(email.trim());
        setNotice("If an account exists for that email, we've sent a reset link. Check your inbox.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const titles = { signin: "Welcome back", signup: "Create your account", forgot: "Reset your password" };

  return (
    <Dialog open={authModalOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-[420px]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-brand">{titles[mode]}</DialogTitle>
        </DialogHeader>

        {mode !== "forgot" && (
          <>
            <button onClick={() => login()} data-testid="auth-google-btn"
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-foreground/80 transition-colors hover:bg-secondary">
              <GoogleIcon /> Continue with Google
            </button>
            <div className="my-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or {mode === "signin" ? "sign in" : "sign up"} with email <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (optional)"
                data-testid="auth-name-input"
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
              data-testid="auth-email-input"
              className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
          </div>
          {mode !== "forgot" && (
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "Create a password (8+ chars, incl. a number/symbol)" : "Password"}
                data-testid="auth-password-input"
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-sm font-medium text-red-600">{error}</p>}
          {notice && <p data-testid="auth-notice" className="text-sm font-medium text-brand">{notice}</p>}

          <button type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-action px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-action-hover disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
          </button>
        </form>

        <div className="mt-1 space-y-1 text-center text-sm text-muted-foreground">
          {mode === "signin" && (
            <>
              <p>
                <button onClick={() => switchMode("forgot")} data-testid="auth-forgot-link" className="font-medium text-brand hover:underline">Forgot password?</button>
              </p>
              <p>No account? <button onClick={() => switchMode("signup")} data-testid="auth-switch-signup" className="font-semibold text-brand hover:underline">Create one</button></p>
            </>
          )}
          {mode === "signup" && (
            <p>Already have an account? <button onClick={() => switchMode("signin")} data-testid="auth-switch-signin" className="font-semibold text-brand hover:underline">Sign in</button></p>
          )}
          {mode === "forgot" && (
            <p><button onClick={() => switchMode("signin")} data-testid="auth-back-signin" className="font-semibold text-brand hover:underline">Back to sign in</button></p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
